import React, { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '../services/api';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from './ui/command';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

/**
 * Searchable client combobox with an inline "+ Add new client" quick-create.
 *
 * Props:
 *   clients         — array of existing client objects ({ user_id, name, client_type, company_name, phone, email })
 *   value           — selected client user_id
 *   onChange(id)    — called with the new selected user_id
 *   onClientCreated(client) — fired after a brand-new client is created via the quick-add form
 */
const ClientPicker = ({ clients = [], value, onChange, onClientCreated }) => {
  const [open, setOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const selected = useMemo(
    () => clients.find((c) => c.user_id === value) || null,
    [clients, value]
  );

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            data-testid="client-picker-trigger"
          >
            <span className="truncate text-left">
              {selected ? (
                <>
                  {selected.name}
                  <span className="text-xs text-muted-foreground ml-1">
                    {selected.client_type === 'dealer' ? '(Dealer)' : '(Individual)'}
                  </span>
                </>
              ) : (
                <span className="text-muted-foreground inline-flex items-center gap-2">
                  <Search className="w-3.5 h-3.5" />
                  Search & select client
                </span>
              )}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]"
          align="start"
        >
          <Command
            filter={(itemValue, search) => {
              // itemValue is the haystack we pass in; cmdk does its own
              // fuzzy filtering but we lower-case both sides for safety.
              if (!search) return 1;
              return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
            }}
          >
            <CommandInput
              placeholder="Search by name, phone, company…"
              data-testid="client-picker-search"
            />
            <CommandList className="max-h-72">
              <CommandEmpty>No client found.</CommandEmpty>
              <CommandGroup heading={`Clients (${clients.length})`}>
                {clients.map((c) => {
                  const label = [
                    c.name,
                    c.company_name,
                    c.phone,
                    c.email,
                    c.client_type,
                  ].filter(Boolean).join(' · ');
                  return (
                    <CommandItem
                      key={c.user_id}
                      value={label}
                      onSelect={() => {
                        onChange(c.user_id);
                        setOpen(false);
                      }}
                      data-testid={`client-picker-item-${c.user_id}`}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === c.user_id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="flex flex-col">
                        <span className="text-sm">{c.name}</span>
                        <span className="text-[11px] text-muted-foreground">
                          {c.client_type === 'dealer'
                            ? `Dealer${c.company_name ? ` · ${c.company_name}` : ''}`
                            : 'Individual'}
                          {c.phone ? ` · ${c.phone}` : ''}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  value="__add_new_client__"
                  onSelect={() => {
                    setOpen(false);
                    setQuickAddOpen(true);
                  }}
                  data-testid="client-picker-add-new"
                  className="text-primary"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add new client
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <QuickAddClientDialog
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        onCreated={(newClient) => {
          onClientCreated?.(newClient);
          // Auto-select the newly created client
          if (newClient?.user_id) onChange(newClient.user_id);
          setQuickAddOpen(false);
        }}
      />
    </>
  );
};

/** Minimal client-creation form used inline from the New Order dialog. */
const QuickAddClientDialog = ({ open, onOpenChange, onCreated }) => {
  const empty = {
    client_type: 'individual',
    surname: '',
    given_name: '',
    email: '',
    phone: '',
    company_name: '',
    nin_number: '',
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const set = (patch) => setForm((p) => ({ ...p, ...patch }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.surname.trim() || !form.given_name.trim()) {
      toast.error('Surname and Given Name are required'); return;
    }
    if (!form.email.trim() || !form.phone.trim()) {
      toast.error('Email and Phone are required'); return;
    }
    setSaving(true);
    try {
      const payload = {
        client_type: form.client_type,
        surname: form.surname.trim(),
        given_name: form.given_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      };
      if (form.client_type === 'dealer' && form.company_name.trim()) {
        payload.company_name = form.company_name.trim();
      }
      if (form.client_type === 'individual' && form.nin_number.trim()) {
        payload.nin_number = form.nin_number.trim();
      }
      const res = await createClient(payload);
      toast.success('Client created');
      setForm(empty);
      onCreated?.(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === 'object') {
        const matched = (detail.matched_on || []).join(', ');
        toast.error(`Duplicate client (matched on: ${matched || 'identifier'})`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create client');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="quick-add-client-dialog">
        <DialogHeader>
          <DialogTitle>Quick Add Client</DialogTitle>
          <DialogDescription>
            Create a new client without leaving the New Order form. The client will be
            auto-selected once created.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Client Type *</Label>
              <Select
                value={form.client_type}
                onValueChange={(v) => set({ client_type: v })}
              >
                <SelectTrigger data-testid="quick-client-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="dealer">Dealer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.client_type === 'dealer' ? (
              <div className="space-y-1.5">
                <Label>Company Name</Label>
                <Input
                  value={form.company_name}
                  onChange={(e) => set({ company_name: e.target.value })}
                  data-testid="quick-client-company"
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>NIN <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Input
                  value={form.nin_number}
                  onChange={(e) => set({ nin_number: e.target.value })}
                  data-testid="quick-client-nin"
                />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Surname *</Label>
              <Input
                value={form.surname}
                onChange={(e) => set({ surname: e.target.value })}
                required
                data-testid="quick-client-surname"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Given Name *</Label>
              <Input
                value={form.given_name}
                onChange={(e) => set({ given_name: e.target.value })}
                required
                data-testid="quick-client-given-name"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
                required
                data-testid="quick-client-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Phone *</Label>
              <Input
                value={form.phone}
                onChange={(e) => set({ phone: e.target.value })}
                required
                data-testid="quick-client-phone"
              />
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="quick-client-submit">
              {saving ? 'Creating…' : 'Create & Select'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ClientPicker;
